import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Plus, X, ChevronLeft, ChevronRight, Image, Video, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

/* ===== Story Viewer (fullscreen overlay) ===== */
const StoryViewer = ({ storyGroups, initialGroupIdx, onClose }) => {
  const [groupIdx, setGroupIdx] = useState(initialGroupIdx);
  const [storyIdx, setStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const group = storyGroups[groupIdx];
  const story = group?.stories[storyIdx];

  useEffect(() => {
    setStoryIdx(0);
    setProgress(0);
  }, [groupIdx]);

  useEffect(() => {
    if (!story) return;
    // Mark as viewed
    axios.post(`${API}/stories/${story.story_id}/view`, null, { withCredentials: true }).catch(() => {});
    // Auto-advance after 5s
    setProgress(0);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(elapsed / 5000, 1));
      if (elapsed >= 5000) {
        clearInterval(timerRef.current);
        goNext();
      }
    }, 50);
    return () => clearInterval(timerRef.current);
  }, [groupIdx, storyIdx]);

  const goNext = () => {
    if (storyIdx < group.stories.length - 1) {
      setStoryIdx(prev => prev + 1);
    } else if (groupIdx < storyGroups.length - 1) {
      setGroupIdx(prev => prev + 1);
    } else {
      onClose();
    }
  };

  const goPrev = () => {
    if (storyIdx > 0) {
      setStoryIdx(prev => prev - 1);
    } else if (groupIdx > 0) {
      setGroupIdx(prev => prev - 1);
    }
  };

  if (!story) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black flex items-center justify-center" data-testid="story-viewer">
      {/* Close button */}
      <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 bg-white/20 rounded-full text-white hover:bg-white/30">
        <X className="h-6 w-6" />
      </button>

      {/* Progress bars */}
      <div className="absolute top-2 left-4 right-4 flex gap-1 z-10">
        {group.stories.map((_, i) => (
          <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all"
              style={{
                width: i < storyIdx ? '100%' : i === storyIdx ? `${progress * 100}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Author info */}
      <div className="absolute top-6 left-4 z-10 flex items-center gap-3">
        <Avatar className="h-10 w-10 border-2 border-white">
          <AvatarImage src={resolveUrl(group.author_avatar)} />
          <AvatarFallback className="bg-purple-600 text-white">{group.author_name?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-white font-medium text-sm">{group.author_name}</p>
          <p className="text-white/60 text-xs">
            {story.created_at ? new Date(story.created_at).toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
          </p>
        </div>
      </div>

      {/* Navigation areas */}
      <div className="absolute inset-0 flex z-[5]">
        <div className="w-1/3 h-full cursor-pointer" onClick={goPrev} />
        <div className="w-1/3 h-full" />
        <div className="w-1/3 h-full cursor-pointer" onClick={goNext} />
      </div>

      {/* Nav arrows */}
      {(groupIdx > 0 || storyIdx > 0) && (
        <button onClick={goPrev} className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 rounded-full text-white hover:bg-white/30 z-10">
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      {(groupIdx < storyGroups.length - 1 || storyIdx < group.stories.length - 1) && (
        <button onClick={goNext} className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/20 rounded-full text-white hover:bg-white/30 z-10">
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Story content */}
      <div className="w-full h-full flex items-center justify-center">
        {story.media_type === 'video' ? (
          <video src={resolveUrl(story.media_url)} className="max-w-full max-h-full object-contain" autoPlay muted playsInline />
        ) : (
          <img src={resolveUrl(story.media_url)} alt="" className="max-w-full max-h-full object-contain" />
        )}
      </div>

      {/* Caption */}
      {story.caption && (
        <div className="absolute bottom-8 left-4 right-4 z-10">
          <p className="text-white text-center text-lg font-medium drop-shadow-lg bg-black/30 rounded-xl px-4 py-2 backdrop-blur-sm">
            {story.caption}
          </p>
        </div>
      )}
    </div>
  );
};

/* ===== Stories Bar (carousel at top of feed) ===== */
const StoriesBar = () => {
  const { user } = useAuth();
  const [storyGroups, setStoryGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerGroupIdx, setViewerGroupIdx] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const { data } = await axios.get(`${API}/stories`, { withCredentials: true });
      setStoryGroups(data.story_groups || []);
    } catch {} finally { setLoading(false); }
  };

  const handleCreateStory = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResp = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      if (uploadResp.data.file_url) {
        const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
        await axios.post(`${API}/stories`, {
          media_url: uploadResp.data.file_url,
          media_type: mediaType,
          caption: ''
        }, { withCredentials: true });
        loadStories();
      }
    } catch (err) { console.error('Story upload error:', err); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const openViewer = (idx) => {
    setViewerGroupIdx(idx);
    setViewerOpen(true);
  };

  const ownGroup = storyGroups.find(g => g.user_id === user?.user_id);
  const hasOwnStory = !!ownGroup;

  return (
    <>
      <input type="file" ref={fileInputRef} onChange={handleCreateStory} className="hidden" accept="image/*,video/*" />
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 mb-6" data-testid="stories-bar">
        <div className="flex items-center gap-4 overflow-x-auto scrollbar-hide" ref={scrollRef}>
          {/* Create Story Button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center gap-1.5 flex-shrink-0"
            disabled={uploading}
            data-testid="create-story-btn"
          >
            <div className="relative">
              <Avatar className="h-16 w-16 border-2 border-dashed border-purple-400">
                <AvatarImage src={resolveUrl(user?.avatar)} />
                <AvatarFallback className="bg-purple-50 text-purple-600">{user?.display_name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center border-2 border-white">
                {uploading ? <Loader2 className="h-3 w-3 text-white animate-spin" /> : <Plus className="h-3 w-3 text-white" />}
              </div>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium max-w-[64px] truncate">Ma story</span>
          </button>

          {/* Story Circles */}
          {storyGroups.map((group, idx) => (
            <button
              key={group.user_id}
              onClick={() => openViewer(idx)}
              className="flex flex-col items-center gap-1.5 flex-shrink-0"
              data-testid={`story-circle-${group.user_id}`}
            >
              <div className={`p-0.5 rounded-full ${group.has_unviewed
                ? 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400'
                : 'bg-gray-300 dark:bg-gray-600'
              }`}>
                <Avatar className="h-16 w-16 border-2 border-white dark:border-gray-800">
                  <AvatarImage src={resolveUrl(group.author_avatar)} />
                  <AvatarFallback className="bg-gray-100 text-gray-600">{group.author_name?.[0]}</AvatarFallback>
                </Avatar>
              </div>
              <span className="text-xs text-gray-600 dark:text-gray-400 font-medium max-w-[64px] truncate">
                {group.user_id === user?.user_id ? 'Ma story' : group.author_name?.split(' ')[0]}
              </span>
            </button>
          ))}

          {!loading && storyGroups.length === 0 && (
            <div className="flex items-center gap-2 text-gray-400 text-sm pl-2">
              <Image className="h-4 w-4" />
              <span>Publiez votre premiere story !</span>
            </div>
          )}
        </div>
      </div>

      {viewerOpen && storyGroups.length > 0 && (
        <StoryViewer
          storyGroups={storyGroups}
          initialGroupIdx={viewerGroupIdx}
          onClose={() => { setViewerOpen(false); loadStories(); }}
        />
      )}
    </>
  );
};

export default StoriesBar;
